import {
  BrandingBackgroundMode,
  GetAllResolvedEdiblesResponse,
  GetMenuAppRestaurantDataResponse,
  Menu,
  Restaurant,
} from "ts-packages/api-types/src";

import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Theme } from "@mui/material";

import { useMenuApp } from "../hooks/useMenuApp";

type MenuItem = { menuId: string; menuName: string };

interface RestaurantContextProps {
  restaurant?: Restaurant;
  brandName?: string;
  backgroundMode?: BrandingBackgroundMode;
  backgroundImageUrl?: string;
  logoImageUrl?: string;
  theme?: Theme;
  menuId?: string;
  menu?: Menu;
  linkedItems?: GetAllResolvedEdiblesResponse | undefined;
  menusInDisplayOrder?: MenuItem[];
  isLoading: boolean;
  priceFormatter?: (priceInLowestDenomination: number) => string;
  onMenuSelect?: (menuId?: string) => void;
  hashedStaffAppPIN?: string;
}

interface RestaurantProviderProps
  extends PropsWithChildren<{
    restaurantData: GetMenuAppRestaurantDataResponse;
    urlSlug: string;
    menuId?: string;
  }> {}

export const RestaurantContext = createContext<RestaurantContextProps>({
  isLoading: true,
});

export const RestaurantProvider = ({
  children,
  restaurantData,
  urlSlug,
}: RestaurantProviderProps) => {
  const [menuItems, setMenuItems] = useState<MenuItem[]>();
  const [activeMenuId, setActiveMenuId] = useState<string>();
  const {
    priceFormatter,
    restaurant,
    brandName,
    restaurantBrandingAndTheme,
    menuResponse,
    linkedItems,
  } = useMenuApp({
    restaurantData,
    urlSlug,
    menuId: activeMenuId,
  });

  const { theme, backgroundMode, backgroundImageUrl, logoImageUrl } =
    restaurantBrandingAndTheme;

  const handleChangeMenu = useCallback((menuId?: string) => {
    setActiveMenuId(menuId);
  }, []);

  useEffect(() => {
    if (!menuItems && menuResponse.data?.menusInDisplayOrder) {
      setMenuItems(menuResponse.data.menusInDisplayOrder);
    }
  }, [menuItems, menuResponse]);

  const value = useMemo(
    () => ({
      priceFormatter: priceFormatter,
      menu: menuResponse?.data?.menu,
      restaurant: restaurant,
      brandName,
      backgroundMode: backgroundMode,
      backgroundImageUrl: backgroundImageUrl,
      logoImageUrl: logoImageUrl,
      menuId: activeMenuId,
      menusInDisplayOrder: menuItems,
      linkedItems,
      theme,
      isLoading: menuResponse.isLoading,
      onMenuSelect: handleChangeMenu,
      hashedStaffAppPIN: restaurantData?.hashedStaffAppPIN,
    }),
    [
      restaurant,
      brandName,
      backgroundMode,
      backgroundImageUrl,
      logoImageUrl,
      menuResponse,
      activeMenuId,
      linkedItems,
      theme,
      menuItems,
      priceFormatter,
      handleChangeMenu,
      restaurantData?.hashedStaffAppPIN,
    ]
  );

  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
};
